import { useCallback, useEffect, useRef } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import { isLanguagePreset, PRESET_VOICE_FIELDS } from '@/constants/voicePresets';
import { IntegrationType, type VoicePreset } from '@/shared/types/integration';

import { useProviderLanguage } from './useProviderLanguage';
import { useVoicePresets, type VoiceChannel } from './useVoicePresets';

// Maps the voice integration type to the preset channel served by the orchestrator.
const CHANNEL_BY_TYPE: Partial<Record<IntegrationType, VoiceChannel>> = {
  [IntegrationType.WEB_VOICE]: 'web',
  [IntegrationType.TWILIO_VOICE]: 'twilio',
  [IntegrationType.TELNYX_VOICE]: 'telnyx',
};

// Fields a preset can own (+ the language selector). A change to any of these is what could pull
// the config away from (or back to) a preset bundle.
const PRESET_FIELD_PREFIXES = [
  'config.language',
  ...PRESET_VOICE_FIELDS.map((field) => `config.${field}`),
];

const isPresetField = (name: string) =>
  PRESET_FIELD_PREFIXES.some((prefix) => name.startsWith(prefix));

// Subset deep-equality: every leaf the bundle specifies must equal the same leaf in the config.
// Extra keys the form carries (provider defaults, inactive-provider subtrees) are ignored — the
// preset only "owns" the fields it set, so only those define a match.
const matchesSubset = (actual: unknown, expected: unknown): boolean => {
  if (Array.isArray(expected)) {
    // Element-wise, order-preserving (order matters for e.g. language_hints) — recursion handles
    // nested objects and undefined leaves that JSON.stringify would mishandle.
    return (
      Array.isArray(actual) &&
      actual.length === expected.length &&
      expected.every((item, index) => matchesSubset(actual[index], item))
    );
  }
  if (expected !== null && typeof expected === 'object') {
    if (actual === null || typeof actual !== 'object') {
      return false;
    }
    const actualObj = actual as Record<string, unknown>;
    return Object.entries(expected).every(([key, value]) => matchesSubset(actualObj[key], value));
  }
  return actual === expected;
};

/**
 * Drives the voice Configuration preset selector.
 *
 * - `applyPreset(language)` writes the fetched bundle (STT/TTS/denoiser + language, cascaded to
 *   every provider via `useProviderLanguage`) and records the preset.
 * - `applyPreset('custom')` keeps current values, just marks the preset custom.
 * - The selected preset is kept honest by diffing: as soon as the live config no longer matches the
 *   selected preset's bundle (a user edited an auto-filled field), it flips to 'custom'.
 */
export const useVoicePreset = () => {
  const { watch, setValue, getValues } = useFormContext();
  const { setLanguage } = useProviderLanguage();
  const integrationType = useWatch({ name: 'type' }) as IntegrationType | undefined;
  const { presets, isLoading } = useVoicePresets(
    integrationType ? CHANNEL_BY_TYPE[integrationType] : undefined
  );

  const preset = (useWatch({ name: 'config.preset' }) as VoicePreset) ?? 'custom';

  // Guards the diff subscription against applyPreset's own multi-field write burst (which would
  // otherwise transiently flip to custom mid-apply). setValue + watch are synchronous, so a plain
  // flag covers the whole burst.
  const isApplying = useRef(false);

  // Flips the selected preset to 'custom' when the live config no longer matches its bundle.
  // `markDirty` is false for passive reconciliation (load/bundle change) so a stale label gets
  // corrected without spuriously enabling Save; true when a user edit caused the drift.
  const reconcilePreset = useCallback(
    (markDirty: boolean) => {
      const current = getValues('config.preset') as VoicePreset;
      if (!isLanguagePreset(current)) {
        return;
      }
      const bundle = presets?.[current];
      if (!bundle) {
        return;
      }
      const matches = Object.entries(bundle).every(([key, value]) =>
        matchesSubset(getValues(`config.${key}`), value)
      );
      if (!matches) {
        setValue('config.preset', 'custom', { shouldDirty: markDirty });
      }
    },
    [presets, getValues, setValue]
  );

  const applyPreset = useCallback(
    (id: VoicePreset) => {
      if (id === 'custom') {
        setValue('config.preset', 'custom', { shouldDirty: true });
        return;
      }
      const bundle = isLanguagePreset(id) ? presets?.[id] : undefined;
      if (!bundle) {
        return;
      }
      isApplying.current = true;
      try {
        const { language, ...config } = bundle;
        // Cascade FIRST so every provider (including inactive ones not in the bundle) gets a
        // language code; the bundle write then overwrites the active providers with the
        // orchestrator's canonical per-language values, which win.
        setLanguage(language);
        for (const [key, value] of Object.entries(config)) {
          setValue(`config.${key}`, value, { shouldDirty: true });
        }
        setValue('config.preset', id, { shouldDirty: true });
      } finally {
        isApplying.current = false;
      }
    },
    [presets, setLanguage, setValue]
  );

  // Reconcile on load (and when presets resolve or the saved preset changes): a stored preset whose
  // values drifted from the current bundle (bundle change, legacy/hand-edited config) must show as
  // custom without waiting for the user to touch a field. Re-running after a flip is a no-op since
  // 'custom' isn't a language preset.
  useEffect(() => {
    reconcilePreset(false);
  }, [reconcilePreset, preset]);

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (isApplying.current || !name || !isPresetField(name)) {
        return;
      }
      reconcilePreset(true);
    });
    return () => subscription.unsubscribe();
  }, [watch, reconcilePreset]);

  // Disable the selector while loading and on fetch error (presets undefined): applyPreset would
  // otherwise silently no-op on a missing bundle.
  return { preset, applyPreset, isLoading, disabled: isLoading || !presets };
};
